import React, { useMemo } from 'react';
import PhoneInput, { getCountryCallingCode } from 'react-phone-number-input';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';
import Select from './Select';
import { useLanguage } from '../../contexts';
import { useMoney } from '../../hooks/useMoney';

/* Country dial code select for PhoneInput
   - 공통 Select 컴포넌트(grouped options + portal dropdown) 재사용
   - 모달 안에서도 z-index 9999로 잘 보임 */
// 주요 국가 우선 정렬
const PRIORITY_COUNTRIES = ['KR', 'JP', 'CN', 'TW', 'US', 'CA', 'HK', 'VN', 'TH', 'PH', 'SG', 'MY'];

function CountryDialSelect({ value, onChange, options, iconComponent: _Ic }) {
  const { t } = useTranslation();

  // 공통 Select에 grouped options 형태로 넘김
  // - label   : 드롭다운 옵션에 표시되는 풀텍스트 ("+82 대한민국")
  // - shortLabel : trigger(선택 후)에 표시되는 짧은 코드 ("+82")
  const groupedOptions = useMemo(() => {
    const validOptions = options.filter((o) => !o.divider && o.value);
    const buildOpt = (o) => {
      const code = getCountryCallingCode(o.value);
      return { value: o.value, label: `+${code} ${o.label}`, shortLabel: `+${code}` };
    };
    const priority = PRIORITY_COUNTRIES
      .map((code) => validOptions.find((o) => o.value === code))
      .filter(Boolean)
      .map(buildOpt);
    const rest = validOptions
      .filter((o) => !PRIORITY_COUNTRIES.includes(o.value))
      .sort((a, b) => a.label.localeCompare(b.label, 'ko'))
      .map(buildOpt);
    return [
      { label: t('common.main_countries'), options: priority },
      { label: t('common.all_countries'), options: rest },
    ];
  }, [options, t]);

  return (
    <div className="w-[132px] shrink-0">
      <Select
        value={value || ''}
        onChange={(v) => onChange(v || undefined)}
        options={groupedOptions}
        placeholder={t('common.country_select')}
      />
    </div>
  );
}

/*
  Figma asset: TextBox variants
  variant: 'text' | 'number' | 'calendar' | 'time' | 'search' | 'textarea' | 'phone' | 'select'
*/
// 모바일 가독성 살짝 보강 (text-[15px] / semibold) — 데스크탑은 기존 text-sm / medium 유지.
const inputBaseStatic = 'w-full h-12 px-3 border rounded-lg text-[15px] font-semibold sm:text-sm sm:font-medium text-mono-900 placeholder:text-mono-400 hover:border-mono-300 focus:outline-none transition';
const inputBase = `${inputBaseStatic} border-mono-100 focus:border-orange-500`;
export const baseInputClass = `${inputBase} bg-mono-50`;

// BugHerd #130: `required` 가 그동안 props 로 흘러들어가 HTML required 속성을 만들었음.
// → 빈 입력 submit 시 브라우저 native "Please fill out this field" 툴팁이 노출됨.
// 정책: 모든 검증은 호출처 JS 핸들러로 통일. required 는 표시(*) + ARIA 만 담당.
//
// `required` prop 동작:
//  - true  → 라벨 뒤 빨간 별표(*) 인라인, aria-required="true"
//  - HTML required 속성은 절대 추가 안 함 (native tooltip 차단)
//  - 검증은 호출처에서 직접 (예: onSubmit 핸들러에서 alert/error 표시)
function RequiredStar() {
  return <span className="text-red-500 ml-0.5" aria-hidden="true">*</span>;
}

export default function TextBox({
  label, placeholder, value, onChange, variant = 'text',
  icon, leftIcon, min = 0, max, unit, price, baseQty, options, optgroups,
  countryCode = '+82', onCountryChange,
  rows = 4, maxLength, disabled, bg, className = '',
  error, hint, required = false,
  ...props
}) {
  // required 는 위에서 명시적으로 destructure 했으므로 props 에 포함되지 않는다.
  // 결과적으로 {...props} spread 시 HTML required 속성이 input 으로 새지 않음.
  const { t } = useTranslation();
  const { phoneLocale } = useLanguage();
  const money = useMoney();
  const borderClass = error
    ? 'border-red-400 focus:border-red-400'
    : 'border-mono-100 focus:border-orange-500';
  const baseInput = `${inputBaseStatic} ${borderClass} ${bg === 'white' ? 'bg-white' : 'bg-mono-50'}`;
  const message = error || hint;
  const messageClass = error ? 'text-red-500' : 'text-mono-400';

  if (variant === 'number') {
    // BugHerd #58: 일부 호출처가 String(form.sort_order) 같이 문자열로 전달.
    // 또는 가격 포맷('1,000') 등 콤마 포함 문자열도 처리해야 +/- 클릭이 정상 동작.
    const numVal = Number(String(value ?? '').replace(/[^0-9]/g, '')) || 0;
    // BugHerd #56/#58: 호출처마다 onChange 시그니처가 다름 (number / event / 둘 다 처리).
    // 어떤 패턴이라도 throw 하지 않도록 onChange 호출을 두 단계 시도:
    //  1) raw number 전달 (modern style): onChange(5)
    //  2) 그 호출에서 'e.target.value' 류 접근으로 throw 하면 synthetic event 로 재시도.
    const safeEmit = (n) => {
      if (typeof onChange !== 'function') return;
      try {
        onChange(n);
      } catch (err) {
        try {
          onChange({ target: { value: String(n) } });
        } catch (err2) {
          // 두 패턴 다 실패하면 콘솔 경고만 남기고 흘림 (UI 깨지지 않게).
          // eslint-disable-next-line no-console
          console.warn('TextBox.number onChange failed for both styles:', err2);
        }
      }
    };
    // BugHerd #56: 큰 숫자(예: 120분) 입력 시 +/- 클릭 반복이 비효율 -> 직접 타이핑 가능한
    // input 으로 교체. 기존 +/- 버튼은 유지.
    // max 미지정 시 기존 동작 유지(상한 없음).
    const maxVal = typeof max === 'number' ? max : Number.POSITIVE_INFINITY;
    const clamp = (n) => Math.min(maxVal, Math.max(min, n));
    const handleDirectInput = (e) => {
      const raw = e.target.value.replace(/[^0-9]/g, '');
      const n = raw === '' ? 0 : clamp(Number(raw));
      safeEmit(n);
    };
    return (
      <div className={`space-y-1.5 ${className}`}>
        {label && <label className="text-2sm font-medium text-mono-600">{label}{required && <RequiredStar />}</label>}
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-mono-50 border border-mono-100 rounded-lg overflow-hidden">
            <button onClick={() => safeEmit(Math.max(min, numVal - 1))}
              disabled={numVal <= min}
              className="w-12 h-12 flex items-center justify-center text-mono-400 hover:bg-orange-50 hover:text-orange-500 transition disabled:opacity-30">
              <Icon name="minus" size={16} />
            </button>
            <input
              type="text"
              inputMode="numeric"
              value={String(numVal)}
              onChange={handleDirectInput}
              className="w-12 h-12 text-center text-sm font-bold text-mono-900 bg-transparent border-0 focus:outline-none focus:ring-0"
            />
            {unit && <span className="text-sm font-bold text-mono-900 pr-1">{unit}</span>}
            <button onClick={() => safeEmit(clamp(numVal + 1))}
              disabled={numVal >= maxVal}
              className="w-12 h-12 flex items-center justify-center text-mono-400 hover:bg-orange-50 hover:text-orange-500 transition disabled:opacity-30">
              <Icon name="plus" size={16} />
            </button>
          </div>
          {price !== undefined && (
            // baseQty 지정 시 기본 제공 수량 초과분만 가산(예: 차량 정원 초과 인원). 미지정이면 기존대로 전체 수량 × 단가.
            <span className="text-2sm font-medium text-mono-500">( +{money(price * Math.max(0, numVal - (baseQty ?? 0)))} )</span>
          )}
        </div>
      </div>
    );
  }

  if (variant === 'textarea') {
    return (
      <div className={`space-y-1.5 ${className}`}>
        {label && <label className="text-2sm font-medium text-mono-600">{label}{required && <RequiredStar />}</label>}
        <div className="relative">
          <textarea
            value={value} onChange={onChange} placeholder={placeholder}
            rows={rows} maxLength={maxLength} disabled={disabled}
            className={`w-full p-3 rounded-lg text-sm font-medium text-mono-900 placeholder:text-mono-400 focus:outline-none transition resize-none border ${error ? 'border-red-400 focus:border-red-400' : 'border-mono-100 focus:border-orange-500'} bg-mono-50`}
            {...props}
          />
          {maxLength && (
            <span className="absolute bottom-2 right-3 text-xs text-mono-400">
              {(value || '').length}/{maxLength}
            </span>
          )}
        </div>
        {message && <p className={`text-xs ${messageClass}`}>{message}</p>}
      </div>
    );
  }

  if (variant === 'phone') {
    return (
      <div className={`space-y-1.5 ${className}`}>
        {label && <label className="text-2sm font-medium text-mono-600">{label}{required && <RequiredStar />}</label>}
        <PhoneInput
          value={value || undefined}
          onChange={onChange}
          placeholder={placeholder || t('common.phone_input_placeholder')}
          international={false}
          labels={phoneLocale}
          countrySelectComponent={CountryDialSelect}
          numberInputProps={{ className: `flex-1 ${baseInput}`, disabled }}
          className="flex gap-2 w-full"
        />
        {message && <p className={`text-xs ${messageClass}`}>{message}</p>}
      </div>
    );
  }

  if (variant === 'select') {
    return (
      <div className={`space-y-1.5 ${className}`}>
        {label && <label className="text-2sm font-medium text-mono-600">{label}{required && <RequiredStar />}</label>}
        <div className="relative">
          <select value={value} onChange={onChange} disabled={disabled}
            className={`${baseInput} appearance-none pr-10`} {...props}>
            {placeholder && <option value="">{placeholder}</option>}
            {(options || []).map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
            {(optgroups || []).map((group) => (
              <optgroup key={group.label} label={group.label}>
                {group.options.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </optgroup>
            ))}
          </select>
          <Icon name="chevron-down" size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-mono-400 pointer-events-none" />
        </div>
        {message && <p className={`text-xs ${messageClass}`}>{message}</p>}
      </div>
    );
  }

  /* text, calendar, time, search */
  const iconName = variant === 'calendar' ? 'calendar' : variant === 'time' ? 'time' : variant === 'search' ? 'search' : icon;
  const inputType = variant === 'calendar' ? 'date' : variant === 'time' ? 'time' : 'text';

  return (
    <div className={`space-y-1.5 ${className}`}>
      {label && <label className="text-2sm font-medium text-mono-600">{label}{required && <RequiredStar />}</label>}
      <div className="relative">
        {leftIcon && (
          <Icon name={leftIcon} size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-mono-400 pointer-events-none" />
        )}
        <input type={inputType} value={value} onChange={onChange} placeholder={placeholder}
          disabled={disabled} className={`${baseInput} ${leftIcon ? 'pl-10' : ''} ${iconName ? 'pr-10' : ''}`} {...props} />
        {iconName && variant !== 'calendar' && variant !== 'time' && (
          <Icon name={iconName} size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-mono-400 pointer-events-none" />
        )}
      </div>
      {message && <p className={`text-xs ${messageClass}`}>{message}</p>}
    </div>
  );
}
